import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  ALL_EFFECTIVE_ACCESS_PERMISSIONS,
  APPLICATION_RETURN_PERMISSIONS,
  EFFECTIVE_ACCESS_PAGE_DEFINITIONS,
  EFFECTIVE_ACCESS_PAGE_KEY,
} from "common/Constants/EffectiveAccess.constants";
import {
  hasActiveCustomerRole,
  isNonePlatformRole,
  isPlatformAdminProfile,
  platformAdminUsesCustomerContextFromMe,
} from "common/Utilities/Role.utils";
import { filterSelectableCustomerAccessRows } from "common/Utilities/CustomerSelection.utils";

export const NO_EFFECTIVE_ROUTE_ACCESS_MESSAGE =
  "No access available at this moment. Please contact your administrator or try again later.";

export const normalizePermissionSet = (permissions) =>
  new Set(
    (permissions ?? [])
      .map((permission) => String(permission ?? "").trim())
      .filter(Boolean)
  );

export const hasFullEffectivePermissionAccess = (permissions) => {
  const granted = normalizePermissionSet(permissions);
  return ALL_EFFECTIVE_ACCESS_PERMISSIONS.every((permission) =>
    granted.has(permission)
  );
};

export const hasAnyApplicationReturnPermission = (effectiveAccess) => {
  if (hasFullEffectivePermissionAccess(effectiveAccess?.permissions)) {
    return true;
  }
  const granted = normalizePermissionSet(effectiveAccess?.permissions);
  return APPLICATION_RETURN_PERMISSIONS.some((permission) =>
    granted.has(permission)
  );
};

const pagePermissionKeys = (page) => {
  if (Array.isArray(page?.actions) && page.actions.length) {
    return page.actions.flatMap((action) => action.permissions ?? []);
  }
  return page?.permissions ?? [];
};

const resolveActionAccess = (action, granted, hasFullAccess) => {
  const required = action?.permissions ?? [];
  const grantedForAction = required.filter((permission) => granted.has(permission));
  return {
    actionKey: action.actionKey,
    label: action.label,
    hasAccess: hasFullAccess || grantedForAction.length > 0,
    grantedPermissions: grantedForAction,
  };
};

export const resolveEffectivePageAccessList = (effectiveAccess) => {
  const granted = normalizePermissionSet(effectiveAccess?.permissions);
  const hasFullAccess = hasFullEffectivePermissionAccess(
    effectiveAccess?.permissions
  );

  return EFFECTIVE_ACCESS_PAGE_DEFINITIONS.map((page) => {
    const requiredPermissions = pagePermissionKeys(page);
    const grantedPermissions = requiredPermissions.filter((permission) =>
      granted.has(permission)
    );
    const hasAccess = hasFullAccess || grantedPermissions.length > 0;

    const actions = Array.isArray(page.actions)
      ? page.actions.map((action) =>
          resolveActionAccess(action, granted, hasFullAccess)
        )
      : undefined;

    return {
      pageKey: page.pageKey,
      pageLabel: page.pageLabel,
      actionsSummary: page.actionsSummary,
      routeValue: page.routeValue,
      hasAccess,
      grantedPermissions: hasFullAccess ? [...requiredPermissions] : grantedPermissions,
      actions,
    };
  });
};

export const getAccessibleRouteValuesFromEffectiveAccess = (effectiveAccess) => {
  if (hasFullEffectivePermissionAccess(effectiveAccess?.permissions)) {
    return ["all"];
  }

  const pages = resolveEffectivePageAccessList(effectiveAccess);
  return pages
    .filter((page) => page.hasAccess && page.routeValue)
    .map((page) => page.routeValue);
};

export const hasEffectivePageAccess = (effectiveAccess, pageKey) =>
  resolveEffectivePageAccessList(effectiveAccess).find(
    (page) => page.pageKey === pageKey
  )?.hasAccess ?? false;

export const canAccessPageForProfile = (profile, pageKey) =>
  Boolean(profile?.effectiveAccess) &&
  hasEffectivePageAccess(profile.effectiveAccess, pageKey);

export const hasDataManagementAccess = (
  profile,
  { usesEffectiveAccess = false, canAccessPage = () => false } = {}
) => {
  if (usesEffectiveAccess) {
    return canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT);
  }

  const accessTo = (profile?.accessTo ?? []).map((key) =>
    String(key).toLowerCase()
  );
  return accessTo.includes("all") || accessTo.includes("datamanagement");
};

export const normalizeRoutePathname = (pathname = "") => {
  const normalized = String(pathname).split("?")[0].replace(/\/+$/, "");
  return normalized || "/";
};

export const getDataManagementEdgeRoutePath = () =>
  INFRAMARK_CONSTANTS?.navigationRoutes?.edge ?? "/data-management/edge-hierarchy";

export const getUserManagementRoutePath = () =>
  INFRAMARK_CONSTANTS?.navigationRoutes?.userManagement ?? "/user-management";

export const getPlatformCustomersRoutePath = () => "/customers";

export const getPlatformUserAccessRoutePath = () => "/user-access";

export const resolveDefaultPlatformAdminPath = (profile) => {
  if (!profile?.effectiveAccess) {
    return "/access_denied";
  }

  if (
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER
    )
  ) {
    return getPlatformCustomersRoutePath();
  }

  if (
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL
    )
  ) {
    return getPlatformUserAccessRoutePath();
  }

  return "/access_denied";
};

export const resolveDefaultCustomerAdminPath = (profile) => {
  const hasSupportAdmin = hasActiveCustomerRole(profile, "SupportAdmin");
  const hasAdmin = hasActiveCustomerRole(profile, "Admin");

  if (
    hasSupportAdmin &&
    hasEffectivePageAccess(
      profile?.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT
    )
  ) {
    return getDataManagementEdgeRoutePath();
  }

  if (
    hasAdmin &&
    !hasSupportAdmin &&
    hasEffectivePageAccess(
      profile?.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT
    )
  ) {
    return getUserManagementRoutePath();
  }

  if (
    hasEffectivePageAccess(
      profile?.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT
    )
  ) {
    return getDataManagementEdgeRoutePath();
  }
  if (
    hasEffectivePageAccess(
      profile?.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT
    )
  ) {
    return getUserManagementRoutePath();
  }
  return "/access_denied";
};

export const resolveDefaultLandingPath = (profile) => {
  if (
    isPlatformAdminProfile(profile) &&
    !platformAdminUsesCustomerContextFromMe(profile)
  ) {
    if (profile?.sessionFromMe) {
      return resolveDefaultPlatformAdminPath(profile);
    }
    return getPlatformCustomersRoutePath();
  }

  return resolveDefaultCustomerAdminPath(profile);
};

export const isExternalAppUrl = (url = "") =>
  /^https?:\/\//i.test(String(url).trim());

export const getDashboardRedirectUrl = () => {
  const { base_url } = require("common/HttpClient/http-client");
  const normalizedBase = String(base_url ?? "").trim().replace(/\/$/, "");
  return normalizedBase ? `${normalizedBase}/live` : null;
};

export const redirectToLiveApplication = () => {
  const dashboardUrl = getDashboardRedirectUrl();
  if (dashboardUrl) {
    window.location.href = dashboardUrl;
  }
};

export const usesMeEffectiveAccess = (profile) =>
  Boolean(profile?.sessionFromMe && profile?.effectiveAccess);

export const hasSwitchableCustomerAccess = (profile = {}) => {
  const rows = profile?.customerAccess ?? [];
  return filterSelectableCustomerAccessRows(rows).length > 0;
};

export const shouldShowCustomerSwitcher = (profile = {}) => {
  if (!profile?.sessionFromMe) {
    return true;
  }
  if (profile?.effectiveAccess) {
    return true;
  }
  if (profile?.sessionWarning && hasSwitchableCustomerAccess(profile)) {
    return true;
  }
  return false;
};

export const shouldNoneUserUseCustomerAdminLanding = (profile) => {
  if (!isNonePlatformRole(profile) || !usesMeEffectiveAccess(profile)) {
    return false;
  }
  return (
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT
    ) ||
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT
    )
  );
};

export const resolveNoneUserCustomerAdminLandingPath = (profile) => {
  if (!shouldNoneUserUseCustomerAdminLanding(profile)) {
    return null;
  }
  return resolveDefaultCustomerAdminPath(profile);
};

export const hasDashboardPageAccess = (profile) => {
  if (profile?.sessionFromMe && !profile?.effectiveAccess) {
    return false;
  }
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  return (
    hasEffectivePageAccess(profile.effectiveAccess, EFFECTIVE_ACCESS_PAGE_KEY.HOME) ||
    hasAnyApplicationReturnPermission(profile.effectiveAccess)
  );
};

const hasAnyEffectiveAdminPageAccess = (profile) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  const adminPageKeys = [
    EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
    EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    EFFECTIVE_ACCESS_PAGE_KEY.CONFIGURATIONS,
    EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
  ];
  return adminPageKeys.some((pageKey) =>
    hasEffectivePageAccess(profile.effectiveAccess, pageKey)
  );
};

const hasEffectivePageKeysAccess = (profile, pageKeys = []) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  return pageKeys.some((pageKey) =>
    hasEffectivePageAccess(profile.effectiveAccess, pageKey)
  );
};

export const canAccessRouteForProfile = (profile, pathname) => {
  const path = normalizeRoutePathname(pathname);

  if (path === "/confirm-reset-password" || path === "/access_denied") {
    return true;
  }

  if (profile?.sessionFromMe && !profile?.effectiveAccess) {
    return path === "/" || path === "/home";
  }

  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }

  const inPlatformAdminScope =
    isPlatformAdminProfile(profile) &&
    !platformAdminUsesCustomerContextFromMe(profile);

  if (inPlatformAdminScope) {
    if (path === "/" || path === "/home") {
      return (
        hasEffectivePageKeysAccess(profile, [
          EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
          EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
        ]) || hasDashboardPageAccess(profile)
      );
    }
    if (path === "/customers" || path.startsWith("/customers/")) {
      return hasEffectivePageKeysAccess(profile, [
        EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
      ]);
    }
    if (path === "/user-access" || path.startsWith("/user-access/")) {
      return hasEffectivePageKeysAccess(profile, [
        EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
      ]);
    }
    return false;
  }

  if (path === "/" || path === "/home") {
    return (
      hasAnyEffectiveAdminPageAccess(profile) ||
      hasDashboardPageAccess(profile)
    );
  }

  const userManagementPath = normalizeRoutePathname(getUserManagementRoutePath());
  if (
    path === userManagementPath ||
    path.startsWith(`${userManagementPath}/`)
  ) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    ]);
  }

  const dataManagementPath = normalizeRoutePathname(
    INFRAMARK_CONSTANTS?.navigationRoutes?.dataManagement ?? "/data-management"
  );
  if (
    path === dataManagementPath ||
    path.startsWith(`${dataManagementPath}/`)
  ) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
    ]);
  }

  if (path === "/customers" || path.startsWith("/customers/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    ]);
  }

  if (path === "/user-access" || path.startsWith("/user-access/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
    ]);
  }

  if (path === "/dashboard" || path.startsWith("/dashboard/")) {
    return hasDashboardPageAccess(profile);
  }

  if (
    path === "/administration" ||
    path.startsWith("/administration/") ||
    path === "/admin" ||
    path.startsWith("/admin/") ||
    path === "/createcustomer" ||
    path.startsWith("/createcustomer/") ||
    path === "/thingtype" ||
    path.startsWith("/thingtype/")
  ) {
    return false;
  }

  const cloudHierarchy =
    INFRAMARK_CONSTANTS?.navigationRoutes?.cloudHierarchy ?? "/cloud-hierarchy";
  const normalizedCloudPath = normalizeRoutePathname(cloudHierarchy);
  if (path === normalizedCloudPath || path.startsWith(`${normalizedCloudPath}/`)) {
    return hasDashboardPageAccess(profile);
  }

  return false;
};

export const resolveNavigationAfterEffectiveAccessChange = (profile, pathname) => {
  if (!usesMeEffectiveAccess(profile)) {
    return { action: "stay" };
  }

  if (canAccessRouteForProfile(profile, pathname)) {
    return { action: "stay" };
  }

  const defaultPath = resolveDefaultLandingPath(profile);
  if (defaultPath && defaultPath !== "/access_denied") {
    return {
      action: "redirect",
      path: "/",
      useFullPageLoad: false,
    };
  }

  if (hasDashboardPageAccess(profile)) {
    const dashboardUrl = getDashboardRedirectUrl();
    if (dashboardUrl) {
      return {
        action: "redirect",
        path: dashboardUrl,
        useFullPageLoad: true,
      };
    }
  }

  return {
    action: "redirect",
    path: "/",
    useFullPageLoad: false,
  };
};

export const applyNavigationAfterEffectiveAccessChange = (
  profile,
  pathname,
  navigate
) => {
  const resolution = resolveNavigationAfterEffectiveAccessChange(profile, pathname);
  if (resolution.action === "redirect") {
    if (resolution.useFullPageLoad) {
      window.location.assign(resolution.path);
    } else if (typeof navigate === "function") {
      navigate(resolution.path, { replace: true });
    }
  }
  return resolution;
};

export const resolveNavigationAfterCustomerSwitch = (profile) => {
  const defaultPath = usesMeEffectiveAccess(profile)
    ? resolveDefaultCustomerAdminPath(profile)
    : getDataManagementEdgeRoutePath();

  if (isNonePlatformRole(profile)) {
    return {
      action: "redirect",
      path: defaultPath,
      useFullPageLoad: true,
    };
  }

  if (isPlatformAdminProfile(profile)) {
    return {
      action: "redirect",
      path: defaultPath,
      useFullPageLoad: false,
    };
  }

  const resolution = resolveNavigationAfterEffectiveAccessChange(
    profile,
    defaultPath
  );

  if (resolution.action === "stay") {
    return {
      action: "redirect",
      path: defaultPath,
      useFullPageLoad: false,
    };
  }

  return resolution;
};

export const applyNavigationAfterCustomerSwitch = (profile, navigate) => {
  const resolution = resolveNavigationAfterCustomerSwitch(profile);

  if (resolution.action === "redirect") {
    if (resolution.useFullPageLoad) {
      window.location.assign(resolution.path);
      return resolution;
    }
    if (typeof navigate === "function") {
      navigate(resolution.path, { replace: true });
    }
    return resolution;
  }

  if (resolution.action === "no_access" && typeof navigate === "function") {
    navigate(
      usesMeEffectiveAccess(profile)
        ? resolveDefaultLandingPath(profile)
        : getDataManagementEdgeRoutePath(),
      { replace: true }
    );
  }

  return resolution;
};
